import { deriveIntentForPlanningReport, GRID_LED_PLANNING_FIRM_QUALIFICATION_NOTICE } from '../../../../lib/generationImpact/selectedAssessmentIntent';
import React from 'react';
import {
  isPowerFactorRecordedUnknown,
  isPueRecordedUnknown,
  unknownDependentLayers,
} from '../../../../lib/stampBtmGroundingFromIntent';
import type { SiteRagReport } from '../../../../types/siteRag';
// R-FIN-PHASE-D — power-basis foundation. Replaces the bare
// powerRequirementMW="grid MW" assumption with the resolved stack
// (IT load → ×PUE facility → ÷PF MVA + firm-deliverable + reserve).
import {
  formatPowerBandLayer,
  resolveIntentPowerStack,
  resolvePueWithSource,
  resolveIntentPowerStackBand,
  sizingBasisLabel,
} from '../../../../lib/powerStack';
import {
  summarizeIntentFeasibility,
  RESERVE_MARGIN_PCT,
} from '../../../../lib/phaseFeasibility';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { REPORT_TEXT_PX } from '../../../../lib/reportSectionHeading';
import { capacityCreditDeepDiveHasContent } from '../../../../lib/tocEntries';
import { useSectionIdContext } from '../SectionShell';

const MONO = 'var(--report-body-font, inherit)';

const POWER_INTRO_SECTION_IDS = new Set([
  'gridStudy',
  'capacityCreditDeepDive',
  'gridCapacity',
  'interconnectionQueue',
  'rateStructures',
  'curtailmentRisk',
  'renewableIntegration',
  'gridRedundancy',
]);

function powerIntroSectionHasData(report: SiteRagReport, sectionId: string): boolean {
  const pa = report.powerAnalysis;
  const hasElccOrPlanning =
    capacityCreditDeepDiveHasContent(report) || !!report.gridLedStandbyPlanningProjection;
  switch (sectionId) {
    case 'gridStudy':
      return report.reportType === 'feasibility' && hasElccOrPlanning;
    case 'capacityCreditDeepDive':
      return report.reportType !== 'feasibility' && hasElccOrPlanning;
    case 'gridCapacity':
      return !!(pa?.substationAssessment || pa?.capacityAnalysis);
    case 'interconnectionQueue':
      return !!pa?.interconnectionQueue;
    case 'rateStructures':
      return !!pa?.rateStructure;
    case 'curtailmentRisk':
      return !!pa?.curtailmentRisk;
    case 'renewableIntegration':
      return !!pa?.renewableIntegration;
    case 'gridRedundancy':
      return !!pa?.redundancyAssessment;
    default:
      return false;
  }
}

/**
 * Resolve the first visible power section in a routed report deck that mounts
 * `PowerIntentIntroStrip`, so the full Project Context + Power Basis (IT ->
 * facility -> MVA) chip block renders ONCE in the deck rather than repeating
 * 7 times across `CapacityCreditDeepDive`, `GridCapacity`, `InterconnectionQueue`,
 * `RateStructures`, `CurtailmentRisk`, `RenewableIntegration`, and `GridRedundancy`.
 */
function resolveFirstPowerIntroSectionId(report: SiteRagReport): string | undefined {
  const defaultOrder = report.reportType === 'feasibility'
    ? ['gridStudy', 'gridCapacity', 'interconnectionQueue', 'rateStructures', 'curtailmentRisk', 'renewableIntegration', 'gridRedundancy']
    : ['capacityCreditDeepDive', 'gridCapacity', 'interconnectionQueue', 'rateStructures', 'curtailmentRisk', 'renewableIntegration', 'gridRedundancy'];
  const customOrder = Array.isArray(report.sectionOrder) && report.sectionOrder.length > 0
    ? report.sectionOrder.filter(id => POWER_INTRO_SECTION_IDS.has(id))
    : [];
  const candidates = [...customOrder, ...defaultOrder.filter(id => !customOrder.includes(id))];
  const vis = report.sectionVisibility ?? {};
  for (const id of candidates) {
    if (vis[id] === false) continue;
    if (powerIntroSectionHasData(report, id)) return id;
  }
  return candidates[0];
}

/** Round to 1 decimal for compact chip display. */
function r1(mw: number): number {
  return Math.round(mw * 10) / 10;
}

const BTM_LABELS: Record<string, string> = {
  'grid-only': 'Grid-only',
  'btm-only': 'BTM-only',
  hybrid: 'Hybrid grid + BTM',
};

const RENEWABLES_LABELS: Record<string, string> = {
  required: 'Renewables required',
  preferred: 'Renewables preferred',
  agnostic: 'Renewables agnostic',
  avoided: 'Renewables avoided',
};

/** Compact intent/setup callout shown at the top of grid-side power sections. */
export const PowerIntentIntroStrip: React.FC<{
  report: SiteRagReport;
  focus?: 'capacity' | 'timeline' | 'rates' | 'curtailment' | 'renewables' | 'redundancy';
}> = ({ report, focus }) => {
  const theme = useEffectiveReportTheme();
  const sectionIdFromContext = useSectionIdContext();
  const isPrimaryPowerBasisMount =
    !sectionIdFromContext
    || !POWER_INTRO_SECTION_IDS.has(sectionIdFromContext)
    || sectionIdFromContext === resolveFirstPowerIntroSectionId(report);
  const intent = deriveIntentForPlanningReport(report, report.projectIntentAtGen);
  if (report.gridLedStandbyPlanningProjection) {
    if (!isPrimaryPowerBasisMount) return null;
    return (
      <div className="mb-3 px-3 py-2 text-xs" data-testid="power-intent-intro-strip" style={{ color: roleColor(theme, 'body'), borderTop: `1px solid ${roleColor(theme, 'rule')}` }}>
        <div>Selected grid-led standby static planning basis</div>
        <div>{GRID_LED_PLANNING_FIRM_QUALIFICATION_NOTICE}</div>
        {intent?.capacityCreditIntent && <div>Declared accreditation target: {intent.capacityCreditIntent.target}{intent.capacityCreditIntent.targetMW != null ? ` · ${intent.capacityCreditIntent.targetMW} MW` : ' · MW unknown'}; unresolved.</div>}
      </div>
    );
  }
  const prefs = report.typedPreferences?.['power-availability'];
  const mw = intent?.powerRequirementMW;
  const chips: string[] = [];

  // R-FIN-PHASE-D — resolved power stack (IT → facility → MVA). Defensive:
  // resolveIntentPowerStack never throws, but guard the whole strip so a
  // malformed intent can never crash a report view.
  const stack = (() => {
    try { return resolveIntentPowerStack(intent); }
    catch { return null; }
  })();
  // Worst-case firm-deliverable across AND-combined phases (planning-default ELCC).
  const firmSummary = (() => {
    try { return summarizeIntentFeasibility(intent); }
    catch { return null; }
  })();
  // Keep a phase's load and supply joined. The minimum supply across phases
  // is not the supply available to the whole-project/ultimate load above it.
  const matchedPhase = firmSummary?.worstPhaseAssessment;
  const recordedPowerBasis = intent?.powerBasis === 'facility' || intent?.powerBasis === 'it-load';


  // Power-basis chip group (renders below the project-context chips). When the
  // stack resolves we show the IT → facility → MVA breakdown instead of a bare
  // "grid MW" assumption.
  // BUCKET-DERIVED fallback — a range bucket used to suppress the entire chip
  // group, so the power-availability section opened with no stated basis at all.
  const band = (() => {
    if (stack?.facilityMW != null) return null;
    try { return resolveIntentPowerStackBand(intent ?? null); }
    catch { return null; }
  })();

  // Early reports use their frozen modeled ranges, not this header's default
  // point conversion. Mature-report display retains its labelled defaults.
  // A PUE / power factor the user RECORDED as unknown is still applied as the
  // screening default so the stack resolves, but the chips must say so rather
  // than present it (or a layer computed through it) as the project's value.
  // Same rules the SED stamp uses: a declared PUE design target is the user's
  // value, and only the layers computed THROUGH a default are marked.
  const basisStack = stack && stack.facilityMW != null ? stack : band;
  const screening = report.screeningBasis?.stage === 1;
  const pueUnknown = basisStack ? isPueRecordedUnknown(intent, basisStack.pueSource)
    || (screening && ['default', 'config-estimate'].includes(basisStack.pueSource)) : false;
  const pfUnknown = isPowerFactorRecordedUnknown(intent)
    || (screening && !(typeof intent?.powerFactor === 'number' && Number.isFinite(intent.powerFactor)
      && intent.powerFactor > 0 && intent.powerFactor <= 1));
  const dependent = basisStack
    ? unknownDependentLayers(basisStack.basis, pueUnknown, pfUnknown)
    : { itLoad: false, facility: false, apparent: false, generation: false };
  const pueTag = pueUnknown ? ' — unknown, screening default' : '';
  const pfTag = pfUnknown ? ' — unknown, screening default' : '';
  const itTag = dependent.itLoad ? ' — from a screening-default PUE' : '';
  const apparentTag = dependent.apparent && !pfUnknown ? ' — from a screening-default PUE' : '';

  const basisChips: Array<{ label: string; value: string }> = [];
  if (recordedPowerBasis && stack && stack.facilityMW != null) {
    if (stack.itLoadMW != null && (!screening || !dependent.itLoad)) basisChips.push({ label: `IT load${itTag}`, value: `${r1(stack.itLoadMW)} MW` });
    if (!screening || !dependent.facility) basisChips.push({ label: screening && stack.basis === 'facility' ? 'Declared facility load' : `Facility (×PUE ${stack.pue}${pueTag})`, value: `${r1(stack.facilityMW)} MW` });
    if (stack.apparentMVA != null && (!screening || !dependent.apparent)) basisChips.push({ label: `Apparent (÷PF ${stack.powerFactor}${pfTag}${apparentTag})`, value: `${r1(stack.apparentMVA)} MVA` });
    if (!screening) basisChips.push({ label: 'Reserve margin', value: `${RESERVE_MARGIN_PCT}%` });
  } else if (recordedPowerBasis && band) {
    const fmt = (l: Parameters<typeof formatPowerBandLayer>[0]) =>
      formatPowerBandLayer(l, (n) => String(r1(n)));
    if (!screening || !dependent.itLoad) basisChips.push({ label: `IT load (band${itTag})`, value: fmt(band.itLoadMW) });
    if (!screening || !dependent.facility) basisChips.push({ label: screening && band.basis === 'facility' ? 'Declared facility load band' : `Facility (×PUE ${band.pue}, band${pueTag})`, value: fmt(band.facilityMW) });
    if (!screening || !dependent.apparent) basisChips.push({ label: `Apparent (÷PF ${band.powerFactor}, band${pfTag}${apparentTag})`, value: fmt(band.apparentMVA) });
    if (!screening) basisChips.push({
      label: `Sizing basis (${sizingBasisLabel(band.sizingBasisRule)})`,
      value: `${r1(band.facilityMW.sizingBasis)} MW`,
    });
    if (!screening) basisChips.push({ label: 'Reserve margin', value: `${RESERVE_MARGIN_PCT}%` });
  }

  if (!recordedPowerBasis && (stack?.facilityMW != null || band)) {
    basisChips.push({ label: 'Demand basis', value: 'Not recorded; MW is not confirmed facility or IT load' });
  }
  const matchedIntentPhase = matchedPhase ? intent?.phases?.find(phase => phase.id === matchedPhase.phaseId) : undefined;
  const matchedPue = resolvePueWithSource(intent, matchedPhase?.assessment.loadBasis === 'site-fallback' ? undefined : matchedIntentPhase);
  const matchedPueUnknown = intent?.powerBasis === 'it-load' && (isPueRecordedUnknown(intent, matchedPue.source)
    || ['default', 'config-estimate'].includes(matchedPue.source));
  if (recordedPowerBasis && matchedPhase && matchedPhase.assessment.loadBasis !== 'none'
    && matchedPhase.assessment.requiredMW > 0 && !(screening && matchedPueUnknown)) {
    const { phaseLabel, assessment } = matchedPhase;
    basisChips.push({ label: `${phaseLabel} modeled load (${assessment.loadBasisLabel}${matchedPueUnknown ? "; PUE unknown, screening default" : ""})`, value: `${r1(assessment.requiredMW)} MW` });
    basisChips.push({ label: `${phaseLabel} firm-deliverable (ELCC)`, value: `${r1(assessment.firmDeliverableMW)} MW` });
    basisChips.push({ label: `${phaseLabel} required firm capacity`, value: `${r1(assessment.requiredFirmMW)} MW` });
  }

  if (typeof mw === 'number' && Number.isFinite(mw)) {
    chips.push(`${mw} MW target`);
  } else if (band) {
    chips.push(`${band.bucketLabel} target (bucket-derived)`);
  }
  if (prefs?.btmStrategy) chips.push(BTM_LABELS[prefs.btmStrategy] ?? prefs.btmStrategy);
  if (prefs?.renewablesPreference) chips.push(RENEWABLES_LABELS[prefs.renewablesPreference] ?? prefs.renewablesPreference);
  if (prefs?.timeToPowerCriticalDate) chips.push(`COD target ${prefs.timeToPowerCriticalDate}`);
  if (prefs?.curtailmentRiskTolerance) chips.push(`Curtailment tolerance: ${prefs.curtailmentRiskTolerance}`);

  if (chips.length === 0 && basisChips.length === 0) return null;

  const focusNote = (() => {
    switch (focus) {
      case 'capacity':
        if (screening) return 'Compare the declared load and the report’s modeled facility-demand ranges with an applicable utility study. PUE and power-factor assumptions do not establish site demand or available grid headroom.';
        if (stack?.facilityMW != null) {
          return `Grid headroom assessed against the FACILITY load (${r1(stack.facilityMW)} MW) and apparent power${stack.apparentMVA != null ? ` (${r1(stack.apparentMVA)} MVA)` : ''} – not the IT load or a generation nameplate.${dependent.facility || dependent.apparent ? ' These figures rest on a PUE or power factor recorded as unknown (screening default), not a project value.' : ''}`;
        }
        if (band) {
          return `Grid headroom assessed against the FACILITY load band (${formatPowerBandLayer(band.facilityMW, (n) => String(r1(n)))}) – bucket-derived from "${band.bucketLabel}", not an exact declaration, and not the IT load or a generation nameplate.`;
        }
        return mw ? `Assessing whether local grid headroom supports ${mw} MW.` : undefined;
      case 'timeline':
        return prefs?.timeToPowerCriticalDate
          ? `Queue and study timelines evaluated against COD target ${prefs.timeToPowerCriticalDate}.`
          : undefined;
      case 'rates':
        return 'Industrial tariff structure compared against BTM LCOE for hybrid economics.';
      case 'curtailment':
        return prefs?.curtailmentRiskTolerance
          ? `Curtailment exposure assessed against ${prefs.curtailmentRiskTolerance} tolerance.`
          : undefined;
      case 'renewables':
        return prefs?.renewablesPreference
          ? `On-site renewable potential evaluated against ${prefs.renewablesPreference} preference.`
          : undefined;
      case 'redundancy':
        return screening ? 'Assess supply diversity and the largest contingency against the proposed service level; equipment counts alone do not establish redundancy.'
          : 'Redundancy assessed for Tier III/IV uptime under declared BTM strategy.';
      default:
        return undefined;
    }
  })();

  // Theme-aware label (mono small-caps, muted ink) replacing the old
  // app muted-ink Tailwind token so the strip follows the report preset.
  const groupLabelStyle: React.CSSProperties = {
    fontFamily: MONO,
    fontSize: REPORT_TEXT_PX.caption,
    fontWeight: 600,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: roleColor(theme, 'muted'),
  };
  // Hairline-outline chip (replaces the filled boxy app chip) — SQUARE
  // hairline + the editorial mono/numeric face, themed border + body ink,
  // tabular figures for the MW/MVA values. Matches the editorial-primitive
  // chip idiom (EditorialBadge outline: no rounded pill, no fill).
  const chipStyle: React.CSSProperties = {
    border: `1px solid ${roleColor(theme, 'hair')}`,
    color: roleColor(theme, 'body'),
    fontFamily: MONO,
    fontVariantNumeric: 'tabular-nums',
  };

  if (!isPrimaryPowerBasisMount) {
    if (!focusNote || focus === 'capacity') return null;
    return (
      <div
        className="mb-3 px-3 py-2 text-xs"
        style={{
          borderTop: `1px solid ${roleColor(theme, 'rule')}`,
          background: 'transparent',
        }}
        data-testid="power-intent-focus-note"
      >
        <div className="m-0" style={{ color: roleColor(theme, 'body') }}>{focusNote}</div>
      </div>
    );
  }

  return (
    <div
      className="mb-3 px-3 py-2 text-xs"
      // FLATTEN-TO-CREAM — the "Project context" intro strip reads on the cream
      // page; the grey `subtleBg` fill is dropped and the top rule alone
      // separates it (matching the flattened ContextualKeyFindings intent-context
      // chip bar). The chips are already hairline-outlined (no fill).
      style={{
        borderTop: `1px solid ${roleColor(theme, 'rule')}`,
        background: 'transparent',
      }}
      data-testid="power-intent-intro-strip"
    >
      <div className="mb-1" style={groupLabelStyle}>Project context</div>
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chips.map(chip => (
            <span key={chip} className="px-1.5 py-0.5" style={chipStyle}>
              {chip}
            </span>
          ))}
        </div>
      )}
      {basisChips.length > 0 && (
        <div className="mt-1.5" data-testid="power-basis-chip-group">
          <div className="mb-1" style={groupLabelStyle}>Power basis (IT → facility → MVA)</div>
          <div className="flex flex-wrap gap-1.5">
            {basisChips.map(chip => (
              <span
                key={chip.label}
                className="px-1.5 py-0.5"
                style={chipStyle}
              >
                <span style={{ color: roleColor(theme, 'muted') }}>{chip.label}:</span> {chip.value}
              </span>
            ))}
          </div>
          {/* DIVs, not <p> (2026-08-04 deploy triage): the editorial visual
              gate treats a flow section with ≥2 substantial <p>s as packable
              running prose and asserts it must pack into the 2nd page column.
              These strip notes are chip-rail FURNITURE — the paged columniser
              rightly never columnises them — so rendering them as <p> armed
              the col-2 assertion on every power section once the band
              fallback made the strip render for bucket-range intents (the
              v1.22.0 build blocker). Visual output is identical: margins come
              from the classes, not the tag. */}
          <div className="mt-1 mb-0" style={{ color: roleColor(theme, 'muted') }}>
            Generation nameplate is sized separately via ELCC + reserve; capacity-credit values are planning defaults, not a site-specific ELCC study.
          </div>
        </div>
      )}
      {focusNote && <div className="mt-1.5 m-0" style={{ color: roleColor(theme, 'body') }}>{focusNote}</div>}
      {firmSummary?.worstVerdict && firmSummary.worstVerdict !== 'feasible' && firmSummary.worstMessage && (
        <div className="mt-1.5 mb-0" style={{ color: roleColor(theme, 'body') }}>{firmSummary.worstMessage}</div>
      )}
    </div>
  );
};
